"use client";

import { useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogPortal,
} from "@baluu/ui/components/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import type { TermsSettings, CustomTerms, TicketBookingPolicy } from "./utils/types";
import { RenderMarkdown } from "./utils/render-markdown";

export type TicketPolicyEntry = {
  ticketName: string;
  policy: TicketBookingPolicy;
};

export type TermsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  settings?: TermsSettings;
  customTerms?: CustomTerms;
  timezone?: string;
  giftCardDuration?: number;
  isGiftCardEnabled?: boolean;
  ticketPolicies?: TicketPolicyEntry[];
};

// Format date helper
function formatDate(dateString: string | undefined, timezone?: string): string {
  if (!dateString) return "March 2022";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: timezone || "UTC",
    });
  } catch {
    return "March 2022";
  }
}


// Cancellation Policy Section
function CancellationPolicySection({ settings }: { settings?: TermsSettings }) {
  const policy = settings?.cancellationPolicy;

  if (!policy) return null;

  const sortedTiers = policy.cancellationTiers
    ? [...policy.cancellationTiers].sort((a, b) => {
        if (a.daysBefore === b.daysBefore) {
          return b.hoursBefore - a.hoursBefore;
        }
        return b.daysBefore - a.daysBefore;
      })
    : [];

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-foreground">Cancellation Policy</h3>
      {!policy.canCancel ? (
        <p className="text-muted-foreground">You may not cancel your booking.</p>
      ) : (
        <ul className="space-y-1 text-muted-foreground">
          {sortedTiers.map((tier, index) => {
            const daysText =
              tier.daysBefore > 0
                ? `${tier.daysBefore} ${tier.daysBefore > 1 ? "days" : "day"}`
                : null;
            const hoursText =
              tier.hoursBefore > 0
                ? `${tier.hoursBefore} ${tier.hoursBefore > 1 ? "hours" : "hour"}`
                : null;
            const timeText = [daysText, hoursText].filter(Boolean).join(" ");
            const refundText =
              tier.refundPercentage > 0 ? `${tier.refundPercentage}%` : "0%";

            return (
              <li key={index}>
                {timeText} before the event: {refundText} refund
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Default Booking Policy — Additional Terms Section
function DefaultPolicyAdditionalTermsSection({
  settings,
}: {
  settings?: TermsSettings;
}) {
  const additionalTerms = settings?.cancellationPolicy?.additionalTerms;
  if (!additionalTerms) return null;

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-foreground">Terms &amp; Conditions</h3>
      <div className="text-muted-foreground">
        <RenderMarkdown content={additionalTerms} />
      </div>
    </div>
  );
}

// Reschedule Policy Section
function ReschedulePolicySection({ settings }: { settings?: TermsSettings }) {
  const policy = settings?.reschedulePolicy;

  if (!policy) return null;

  const timeParts: string[] = [];
  if (policy.rescheduleDaysBefore && policy.rescheduleDaysBefore > 0) {
    timeParts.push(
      policy.rescheduleDaysBefore > 1
        ? `${policy.rescheduleDaysBefore} days`
        : "1 day"
    );
  }
  if (policy.rescheduleHoursBefore && policy.rescheduleHoursBefore > 0) {
    timeParts.push(
      policy.rescheduleHoursBefore > 1
        ? `${policy.rescheduleHoursBefore} hours`
        : "1 hour"
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-foreground">Reschedule Policy</h3>
      {policy.canReschedule ? (
        <p className="text-muted-foreground">
          If you wish to reschedule an order for a dated event and provided the
          applicable event is not due to take place within the next{" "}
          {timeParts.join(" and ") || "24 hours"}, you can reschedule your
          session for another time for no extra cost.
        </p>
      ) : (
        <p className="text-muted-foreground">You may not reschedule your booking.</p>
      )}
    </div>
  );
}

// Gift Card Terms Section
function GiftCardTermsSection({
  customTerms,
  giftCardDuration = 365,
  isGiftCardEnabled = false,
}: {
  customTerms?: CustomTerms;
  giftCardDuration?: number;
  isGiftCardEnabled?: boolean;
}) {
  if (!isGiftCardEnabled) return null;

  const giftCardMonths =
    {
      90: 3,
      180: 6,
      365: 12,
    }[giftCardDuration] || 12;

  const termsDisplayMode = customTerms?.useCustomGiftCardTerms ?? "default";
  const customGiftCardTerms = customTerms?.giftCards;

  const showDefault =
    termsDisplayMode === "default" || termsDisplayMode === "both";
  const showCustom =
    (termsDisplayMode === "custom" || termsDisplayMode === "both") &&
    customGiftCardTerms;

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-foreground">Gift Cards</h3>
      {showCustom && <RenderMarkdown content={customGiftCardTerms} />}
      {showDefault && (
        <p className="text-muted-foreground">
          You may only cancel a Gift Card within 24 hours of receiving your
          Order Confirmation - and we can offer you a full refund, unless you
          have already used it to make a booking. After 24hrs, Gift Cards are
          non refundable and non-transferable and last for {giftCardMonths}{" "}
          months from the purchase date.
        </p>
      )}
    </div>
  );
}

// Products Terms Section
function ProductsTermsSection({ customTerms }: { customTerms?: CustomTerms }) {
  if (!customTerms?.products) return null;

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-foreground">Products</h3>
      <p className="text-muted-foreground">
        You may cancel a product order provided the product has not already been
        dispatched. If however the product has already been dispatched you will
        not be entitled to a refund or a credit note.
      </p>
    </div>
  );
}

// Private Events Terms Section
function PrivateEventsTermsSection({
  customTerms,
}: {
  customTerms?: CustomTerms;
}) {
  if (!customTerms?.privateEvents) return null;

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-foreground">
        Corporate and Private Events
      </h3>
      <RenderMarkdown content={customTerms.privateEvents} />
    </div>
  );
}

// Per-Ticket Booking Policies Section
function TicketPoliciesSection({ ticketPolicies }: { ticketPolicies?: TicketPolicyEntry[] }) {
  if (!ticketPolicies || ticketPolicies.length === 0) return null;

  // Deduplicate by policy id
  const seen = new Set<string>();
  const uniquePolicies = ticketPolicies.filter(({ policy }) => {
    if (seen.has(policy.id)) return false;
    seen.add(policy.id);
    return true;
  });

  return (
    <div className="space-y-4">
      <h2 className="font-bold text-lg text-foreground">Booking Policies</h2>
      {uniquePolicies.map(({ ticketName, policy }) => {
        const sortedTiers = policy.cancellationTiers
          ? [...policy.cancellationTiers].sort((a, b) => {
              if (a.daysBefore === b.daysBefore) return b.hoursBefore - a.hoursBefore;
              return b.daysBefore - a.daysBefore;
            })
          : [];

        const rescheduleParts: string[] = [];
        if (policy.rescheduleDaysBefore > 0) {
          rescheduleParts.push(
            policy.rescheduleDaysBefore > 1
              ? `${policy.rescheduleDaysBefore} days`
              : "1 day"
          );
        }
        if (policy.rescheduleHoursBefore > 0) {
          rescheduleParts.push(
            policy.rescheduleHoursBefore > 1
              ? `${policy.rescheduleHoursBefore} hours`
              : "1 hour"
          );
        }

        // Tickets using this policy
        const ticketNames = ticketPolicies
          .filter((tp) => tp.policy.id === policy.id)
          .map((tp) => tp.ticketName);

        return (
          <div key={policy.id} className="space-y-2 border-l-2 border-primary/20 pl-4">
            <h3 className="font-semibold text-foreground">
              {policy.name}
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({ticketNames.join(", ")})
              </span>
            </h3>

            {/* Cancellation */}
            {!policy.canCancel ? (
              <p className="text-sm text-muted-foreground">No cancellation allowed.</p>
            ) : (
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground text-xs uppercase tracking-wide mb-1">Cancellation</p>
                <ul className="space-y-0.5">
                  {sortedTiers.map((tier, index) => {
                    const daysText = tier.daysBefore > 0
                      ? `${tier.daysBefore} ${tier.daysBefore > 1 ? "days" : "day"}`
                      : null;
                    const hoursText = tier.hoursBefore > 0
                      ? `${tier.hoursBefore} ${tier.hoursBefore > 1 ? "hours" : "hour"}`
                      : null;
                    const timeText = [daysText, hoursText].filter(Boolean).join(" ");
                    return (
                      <li key={index}>
                        {timeText} before: {tier.refundPercentage}% refund
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Reschedule */}
            {!policy.canReschedule ? (
              <p className="text-sm text-muted-foreground">No reschedule allowed.</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground text-xs uppercase tracking-wide">Reschedule: </span>
                Up to {rescheduleParts.join(" and ") || "24 hours"} before the event.
              </p>
            )}

            {/* Additional terms */}
            {policy.additionalTerms && (
              <div className="text-sm text-muted-foreground mt-2">
                <RenderMarkdown content={policy.additionalTerms} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * TermsDialog - A dialog showing all terms and conditions content
 *
 * Displays the same content as the /terms page:
 * - Custom terms text
 * - Cancellation policy
 * - Reschedule policy
 * - Gift card terms
 * - Products terms
 * - Private events terms
 */
export function TermsDialog({
  isOpen,
  onClose,
  settings,
  customTerms,
  timezone,
  giftCardDuration,
  isGiftCardEnabled,
  ticketPolicies,
}: TermsDialogProps) {
  const updatedDate = formatDate(settings?.termsAndConditions?.updated, timezone);
  const customText = settings?.termsAndConditions?.text;

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        {/* Custom overlay with high z-index to appear above checkout overlay */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        {/* Custom content with high z-index */}
        <DialogPrimitive.Content
          className="fixed top-[50%] left-[50%] z-[9999] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] bg-background rounded-lg border shadow-2xl !p-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onPointerDownOutside={(e) => {
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-10">
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>Order Cancellation Terms and Conditions</DialogTitle>
            <p className="text-sm text-muted-foreground">Updated: {updatedDate}</p>
          </DialogHeader>

          {/* Scrollable content with explicit max height */}
          <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
            <div className="space-y-6 px-6 py-4">
              {/* Custom Terms Text */}
              {customText && (
                <div className="space-y-2">
                  <RenderMarkdown content={customText} />
                </div>
              )}

              {/* Per-Ticket Booking Policies */}
              <TicketPoliciesSection ticketPolicies={ticketPolicies} />

              {/* Events Section (school-level default) — always shown as fallback for tickets without explicit policies */}
              <div className="space-y-4">
                <h2 className="font-bold text-lg text-foreground">
                  {ticketPolicies && ticketPolicies.length > 0 ? "General Policy" : "Events"}
                </h2>
                <CancellationPolicySection settings={settings} />
                <ReschedulePolicySection settings={settings} />
                <DefaultPolicyAdditionalTermsSection settings={settings} />
              </div>

              {/* Products Terms */}
              <ProductsTermsSection customTerms={customTerms} />

              {/* Private Events Terms */}
              <PrivateEventsTermsSection customTerms={customTerms} />

              {/* Gift Card Terms */}
              <GiftCardTermsSection
                customTerms={customTerms}
                giftCardDuration={giftCardDuration}
                isGiftCardEnabled={isGiftCardEnabled}
              />

              {/* Multi-policy note — shown last, after every term section */}
              {settings?.hasMultiplePolicies && (
                <p className="text-xs text-muted-foreground">
                  This is our default policy, some tickets may have their own.
                </p>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/**
 * Hook to manage terms dialog state
 */
export function useTermsDialog() {
  const [isOpen, setIsOpen] = useState(false);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  };
}
